// The standings (scenario H1). Drawn through the kit (LOOKS.md Part 7): the
// row's columns are one class for the header and every row alike.
//
// THE TABLE AND THE RACE (B-387): the table in a panel, the playoff field
// down its edge and the line drawn and named across it; beside it on a
// desktop, under it on a phone, the race for the last seats, the points and
// at Everything the all-play record. It was one table in a panel the width
// of the screen, which on a desktop was five numbers far from their names
// over an empty page.

import { Fragment, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { tiebreakWords, type StandingsRow, type StandingsView } from '@fsp/domain';
import { useAtLeast } from '../data/detail';
import { useLeagueBase, useStandings } from '../data/hooks';
import { TeamMark } from '../kit';
import { Panel } from '../kit/Panel';
import { Button, Stack, Text, Title } from '../kit/ui';
import '../styles/screens/league-screens.css';
import '../styles/screens/standings.css';
import { TitleCrown } from './shared/AwardKit';
import { FromLeague } from './shared/FromLeague';
import { useTier } from '../shell/useTier';
import { SortHead, sortRows, useSort, type SortFirst } from '../kit/SortHead';
import { Term } from '../kit/Why';
import { GLOSSARY, streakWords } from './shared/glossary';
import { useActiveLeague } from '../data/LeaguesProvider';
import {
  AllPlay,
  MaxPoints,
  PlayoffRace,
  PointsRace,
  RaceMark,
  type TableKind,
} from './standings/StandingsSide';
import { Form } from './standings/Form';

/** Games back as the big sites print it: the leader a dash, a half as .5. */
function gamesBackText(gb: number | null): string {
  if (gb === null) return '';
  return gb === 0 ? '—' : String(gb);
}

/**
 * The streak in letters, and in words on a hover. Not a Term of its own: the
 * row is one button (UX-2), and a press inside it would be a button inside a
 * button, its finger's layer reaching over the team's name (the review of
 * 2026-10-17). The column's head says what a streak is, on a tap too.
 */
function Streak({ streak }: { streak: string | null }) {
  return streak ? <abbr title={streakWords(streak)}>{streak}</abbr> : null;
}

/**
 * Whether the playoff field as it stands is the top of the table: then one
 * line under the last seed says it. Under division winners first a leader
 * ranked below the line is in and a better record above it is out, and a
 * line between two rows would say the opposite (the audit of 2026-10-17).
 */
export function fieldIsTheTop(rows: readonly StandingsRow[], count: number): boolean {
  return rows.every((r) => (r.seed !== null) === (r.rank !== null && r.rank <= count));
}

/**
 * A number column: its label and what it means, what each row puts in it,
 * and the figure it sorts by, its own way first (B-377).
 */
interface Column {
  key: string;
  label: string;
  tip: string;
  cell: (r: StandingsRow) => ReactNode;
  sort: (r: StandingsRow) => number | null;
  first: SortFirst;
}

/** A record as a share of games won, a tie half a win: the W–L column's order. */
function winShare(r: StandingsRow): number | null {
  const games = r.wins + r.losses + r.ties;
  return games === 0 ? null : (r.wins + r.ties / 2) / games;
}

const PLACE_TIP = "Each team's place in the table, by the league's tiebreakers.";

/** "W3" as 3, "L2" as −2 and a run of ties as 0: a streak's order, the longest winning first. */
function streakValue(streak: string | null): number | null {
  if (streak === null || streak === '') return null;
  const n = Number(streak.slice(1));
  if (!Number.isFinite(n)) return null;
  return streak.startsWith('W') ? n : streak.startsWith('L') ? -n : 0;
}

export function StandingsScreen() {
  return (
    <FromLeague snapshot={useStandings()}>{(view) => <StandingsBody view={view} />}</FromLeague>
  );
}

function StandingsBody({ view }: { view: StandingsView }) {
  const tier = useTier();
  const standard = useAtLeast('standard');
  const pro = useAtLeast('pro');
  const format = useActiveLeague()?.league.format;
  // THE FORMAT DECIDES THE COLUMNS (Q4, Q5, B-360, B-361). A race on points
  // (total points, elimination) has no record: its points and how far back
  // each team is. All-play's record is the all-play one, and with no
  // opponents it has no points against, no streak and no results.
  const kind: TableKind =
    format?.id === 'points-total' || format?.id === 'elimination'
      ? 'race'
      : format?.id === 'all-play'
        ? 'allplay'
        : 'h2h';
  const cut = view.playoffTeamCount > 0 && view.rows.length > view.playoffTeamCount;
  const ranked = view.rows.every((r) => r.rank !== null);
  // THE SIDE (B-387): the race for the seats, where there are seats to race
  // for; the points from Standard; the all-play record at Everything, in a
  // head-to-head league, whose record is not already it.
  const showRace = kind !== 'race' && view.playoffTeamCount > 0;
  const showPoints = standard && kind !== 'race' && ranked && view.throughPeriod !== null;
  const showAllPlay = pro && kind === 'h2h' && view.rows.some((r) => r.allPlay !== null);
  // MAX PF (B-419) from Standard, where the points are, once a week of facts is written.
  const showMax = standard && kind !== 'race' && view.rows.some((r) => r.maxPoints !== null);
  const side = showRace || showPoints || showAllPlay || showMax;
  const weeksLeft =
    view.regularSeasonEnd === null
      ? null
      : Math.max(0, view.regularSeasonEnd - (view.throughPeriod ?? 0));
  return (
    <div className="fsp-screen fsp-flow">
      <div className="fsp-pinned">
        <Stack gap={1}>
          <Title>Standings</Title>
          <Text size="xs" tone="secondary">
            {view.throughPeriod === null ? 'No results yet' : `Through Week ${view.throughPeriod}`}
            {' · '}
            {/* A LEAGUE WITH NO BRACKET is decided by its table (B-362). */}
            {view.playoffTeamCount === 0
              ? 'No playoffs: the team on top when the season ends wins'
              : cut
                ? `${view.playoffTeamCount} of ${view.rows.length} make the playoffs`
                : 'Every team makes the playoffs'}
          </Text>
          {/* HOW THE RECORD ADDS UP in a league that plays the median (Q3,
              B-359): a week is two games, and a 9–5 after seven weeks needs
              the sentence. */}
          {format?.medianGame ? (
            <Text size="xs" tone="secondary">
              Each record includes a game against the league median every week.
            </Text>
          ) : null}
        </Stack>
      </div>
      <div className={`fsp-duo fsp-st-duo fsp-duo-${tier}${side ? '' : ' fsp-duo-alone'}`}>
        <div className="fsp-duo-main">
          <Table view={view} kind={kind} />
        </div>
        {side ? (
          <div className="fsp-duo-side">
            {showRace ? <PlayoffRace view={view} weeksLeft={weeksLeft} /> : null}
            {showPoints ? <PointsRace view={view} /> : null}
            {showMax ? <MaxPoints view={view} /> : null}
            {showAllPlay ? <AllPlay view={view} /> : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}

/** The table: every team in its place, the playoff field down its edge and its line named. */
function Table({ view, kind }: { view: StandingsView; kind: TableKind }) {
  const base = useLeagueBase();
  const navigate = useNavigate();
  // AT 390 THE NAME IS WHAT MATTERS AND IT WAS WHAT BROKE. The row had six
  // fixed columns totalling ~244px, so a twelve-team league left about 100px
  // for the team name and rendered "Pr…", "M…", "Da…" — a standings table
  // where you cannot tell who is first. CLAUDE.md: what a layout includes
  // scales with the screen, so the phone drops POINTS AGAINST (the one column
  // you never scan on a phone), the last five and games back, and spends it
  // all on the name.
  const phone = useTier() === 'phone';
  const allPlayTip = kind === 'allplay';
  // WHICH NUMBERS RIDE ON THE FACE BY LEVEL (DETAIL_LEVELS.md, Standings):
  // Simple's are the rank and the record; Standard adds the last five, points
  // for and against and the streak; Everything adds games back. The playoff
  // line stays at every level (B-302).
  const standard = useAtLeast('standard');
  const everything = useAtLeast('pro');
  // THE LAST FIVE, from Standard on a tablet up (B-387): the season's form
  // in a glance, as a football table's column carries it. A league with no
  // games against anybody has none.
  const form = standard && !phone && kind === 'h2h';
  const race: (Column | false)[] = [
    {
      key: 'pf',
      label: 'Points',
      tip: GLOSSARY.pointsFor,
      cell: (r) => (
        <Text weight="strong" numeric>
          {r.pointsFor.toFixed(1)}
        </Text>
      ),
      sort: (r) => r.pointsFor,
      first: 'high',
    },
    standard && {
      key: 'back',
      label: 'Back',
      tip: GLOSSARY.pointsBack,
      cell: (r) => (
        <span className="fsp-num fsp-muted">
          {r.pointsBack === null ? '' : r.pointsBack === 0 ? '—' : r.pointsBack.toFixed(1)}
        </span>
      ),
      sort: (r) => r.pointsBack,
      first: 'low',
    },
  ];
  const all: (Column | false)[] = [
    {
      key: 'wl',
      label: 'W–L',
      tip: allPlayTip ? GLOSSARY.allPlayRecord : GLOSSARY.winLoss,
      cell: (r) => (
        <Text weight="strong" numeric>
          {r.wins}–{r.losses}
          {r.ties ? `–${r.ties}` : ''}
        </Text>
      ),
      sort: winShare,
      first: 'high',
    },
    everything &&
      !phone && {
        key: 'gb',
        label: 'GB',
        tip: GLOSSARY.gamesBack,
        cell: (r) => <span className="fsp-num">{gamesBackText(r.gamesBack)}</span>,
        sort: (r) => r.gamesBack,
        first: 'low',
      },
    standard && {
      key: 'pf',
      label: 'PF',
      tip: GLOSSARY.pointsFor,
      cell: (r) => <span className="fsp-num">{r.pointsFor.toFixed(1)}</span>,
      sort: (r) => r.pointsFor,
      first: 'high',
    },
    standard &&
      !phone &&
      kind === 'h2h' && {
        key: 'pa',
        label: 'PA',
        tip: GLOSSARY.pointsAgainst,
        cell: (r) => <span className="fsp-num fsp-muted">{r.pointsAgainst.toFixed(1)}</span>,
        sort: (r) => r.pointsAgainst,
        first: 'high',
      },
    standard &&
      !phone &&
      kind === 'h2h' && {
        key: 'streak',
        label: 'Streak',
        tip: GLOSSARY.streak,
        cell: (r) => (
          <span className="fsp-num">
            <Streak streak={r.streak} />
          </span>
        ),
        sort: (r) => streakValue(r.streak),
        first: 'high',
      },
  ];
  const numbers = (kind === 'race' ? race : all).filter((c): c is Column => c !== false);
  // ONE DEFINITION, used by the header labels and by every row. Two copies is
  // how a label ends up over the wrong number. The widths are the
  // stylesheet's, one set for each tier and level, in `numbers`' order
  // (league-screens.css, standings.css).
  const level = everything ? 'pro' : standard ? 'standard' : 'simple';
  const columns = `fsp-standings-cols fsp-standings-${phone ? 'phone' : 'wide'}-${level}${kind === 'h2h' ? '' : `-${kind}`}${form ? ' fsp-st-with-form' : ''}`;
  const open = (r: StandingsRow) => navigate(`${base}/teams/${r.team.id}`);
  // A HEAD SORTS THE TABLE BY ITS COLUMN (B-377); the rank's head, or a
  // column no longer drawn, puts it back in its own order.
  const order = useSort<string>();
  const by = numbers.find((c) => c.key === order.by) ?? null;
  const cut = view.playoffTeamCount > 0 && view.rows.length > view.playoffTeamCount;
  // The field is the top of the table, or a division winner below it is in;
  // the line is a place in the table's own order only.
  const top = cut && fieldIsTheTop(view.rows, view.playoffTeamCount);
  // DIVISIONS GROUPED (B-419) when the league has them and the table is in its
  // own order: a band over each division's teams, by place within it. The
  // field still runs down the edge; the line, a place in one list, is not
  // drawn across groups.
  const listed = new Set(view.divisions.map((d) => d.id));
  const grouped =
    by === null &&
    view.divisions.length > 1 &&
    view.rows.some((r) => r.divisionId !== null && listed.has(r.divisionId));
  const line = top && by === null && !grouped;
  const sorted = sortRows(view.rows, by?.sort ?? null, by?.first ?? 'high', order.reverse);
  // NOBODY LEFT OUT (the review of B-419): a team with no division, or one
  // the league no longer lists, is its own band at the foot.
  const rows = grouped
    ? [
        ...view.divisions.flatMap((d) => sorted.filter((r) => r.divisionId === d.id)),
        ...sorted.filter((r) => r.divisionId === null || !listed.has(r.divisionId)),
      ]
    : sorted;
  const divisionName = new Map(view.divisions.map((d) => [d.id, d.name]));
  // A ROW'S TIE SENTENCE NAMES THE TEAM UNDER IT in the table's own order
  // (B-457), so it is shown only in that order, in one list.
  const tieNotes = by === null && !grouped;
  // A TIE ON RECORD IS PLACED BY THE TIEBREAKERS (B-419): when two teams are
  // level, the table says so and where the league's tiebreakers are.
  const levelOnRecord =
    kind === 'h2h' &&
    view.rows.some((r, i) =>
      view.rows.some(
        (o, j) =>
          j !== i &&
          r.rank !== null &&
          o.rank !== null &&
          r.wins === o.wins &&
          r.losses === o.losses &&
          r.ties === o.ties &&
          r.wins + r.losses + r.ties > 0,
      ),
    );
  return (
    <Panel title="Table" bodyClass="fsp-st-table">
      {cut && !top ? (
        // DIVISION WINNERS FIRST, and the field is not the top of the table:
        // each team in it says its seed under its name.
        <div className="fsp-sub fsp-ui-wrap-text fsp-st-note">
          Each division’s leader makes the playoffs, then the best records. A seed under a name is
          where that team stands today.
        </div>
      ) : null}
      <div className={`fsp-st-head ${columns}`}>
        {/* The rank's head puts the table back in its own order, once it is
            in another; in its own it only explains. */}
        <span className="fsp-cap">
          {by === null ? (
            <Term tip={PLACE_TIP} name="Place">
              #
            </Term>
          ) : (
            <SortHead
              label="#"
              name="Place"
              tip={PLACE_TIP}
              on={false}
              reverse={false}
              first="low"
              onSort={order.reset}
            />
          )}
        </span>
        <span />
        <span className="fsp-cap">Team</span>
        {form ? (
          <span className="fsp-cap">
            <Term tip={GLOSSARY.lastFive}>Last 5</Term>
          </span>
        ) : null}
        {numbers.map((c) => (
          <span key={c.key} className="fsp-cap fsp-right">
            <SortHead
              label={c.label}
              tip={c.tip}
              on={by?.key === c.key}
              reverse={order.reverse}
              first={c.first}
              onSort={() => order.press(c.key)}
            />
          </span>
        ))}
      </div>
      <div className="fsp-st-rows">
        {rows.map((r, i) => (
          <Fragment key={r.team.id}>
            {grouped && (i === 0 || rows[i - 1]!.divisionId !== r.divisionId) ? (
              <div className="fsp-st-division" role="separator">
                <span>{divisionName.get(r.divisionId ?? '') ?? 'No division'}</span>
              </div>
            ) : null}
            {/* THE LINE, NAMED (B-387): it was a dashed border between two
                rows with a sentence above the table to say what it was. */}
            {line && r.rank === view.playoffTeamCount + 1 ? (
              <div className="fsp-st-line" role="separator" aria-label="Playoff line">
                <span>Playoff line</span>
              </div>
            ) : null}
            <div
              className={`fsp-row fsp-row-clickable fsp-st-row ${columns} ${r.team.isMine ? 'fsp-row-selected' : ''}`}
              // THE FIELD DOWN THE EDGE (B-387), sorted or not: a team in the
              // playoffs as the table stands, by its seed.
              data-in={cut && r.seed !== null ? '' : undefined}
              // THE TEAM, NOT THE TROPHY ROOM (2026-09-24): seeing who has
              // which players is what a standings row is tapped for.
              onClick={() => open(r)}
              // A button answers Enter and Space as well as a click (UX-30).
              onKeyDown={(e) => {
                if (e.target !== e.currentTarget || (e.key !== 'Enter' && e.key !== ' ')) return;
                e.preventDefault();
                open(r);
              }}
              role="button"
              tabIndex={0}
              title={`${r.team.name}'s roster`}
            >
              <span className="fsp-num fsp-muted">{r.rank ?? '—'}</span>
              <TeamMark team={r.team} size={26} />
              <div className="fsp-standings-who">
                <div className="fsp-name">
                  <span className="fsp-name-text">{r.team.name}</span>
                  {r.team.titles ? <TitleCrown titles={r.team.titles} /> : null}
                  <RaceMark status={r.playoffStatus} />
                </div>
                <div className="fsp-sub">
                  {r.team.ownerName}
                  {/* OUT, AND WHEN (Q1, B-364). */}
                  {r.eliminatedPeriod !== null ? ` · out after Week ${r.eliminatedPeriod}` : ''}
                  {cut && !top && r.seed !== null ? ` · seed ${r.seed}` : ''}
                  {/* A phone has no room for the streak's column: it rides
                      under the name from Standard. */}
                  {phone && standard && r.streak ? (
                    <>
                      {' · '}
                      <Streak streak={r.streak} />
                    </>
                  ) : null}
                </div>
                {/* HOW A TIE WAS SETTLED, on the row it put ahead (B-457):
                    words, so at every level, and at Simple without the two
                    figures a points tie gives (B-467): Simple removes
                    numbers. */}
                {tieNotes && r.tiebreakNote !== null ? (
                  <div className="fsp-sub fsp-muted fsp-ui-wrap-text fsp-st-tiebreak">
                    {standard ? r.tiebreakNote : tiebreakWords(r.tiebreakNote)}
                  </div>
                ) : null}
                {/* WHAT HAS TO HAPPEN, in the regular season's last week
                    (B-460): about the team itself, so in any order, and
                    words, so at every level. */}
                {r.raceNote !== null ? (
                  <div className="fsp-sub fsp-ui-wrap-text fsp-st-racenote">{r.raceNote}</div>
                ) : null}
              </div>
              {form ? <Form results={r.results} /> : null}
              {numbers.map((c) => (
                <span key={c.key} className="fsp-right">
                  {c.cell(r)}
                </span>
              ))}
            </div>
          </Fragment>
        ))}
      </div>
      {levelOnRecord ? (
        <div className="fsp-sub fsp-ui-wrap-text fsp-st-note">
          Teams level on record are placed by the league’s tiebreakers.{' '}
          <Button size="xs" variant="quiet" to={`${base}/rules?section=tiebreakers`}>
            Tiebreakers
          </Button>
        </div>
      ) : null}
    </Panel>
  );
}
